/**
 * Turning a pasted Instagram or LinkedIn URL into something the site can show.
 *
 * This is the whole reason the feed integration needs no API keys, no app review and no
 * per-client account connection. Both platforms publish an embed endpoint that renders
 * public content in an iframe, and both take an id that is sitting in the URL a client
 * would copy out of their browser. Private content will not render, which is the correct
 * outcome.
 *
 * Two kinds of link are understood:
 * - a **post** (an Instagram post, reel or TV link; a LinkedIn post), embedded on its own;
 * - a **profile** (an Instagram account; a LinkedIn company or person page), which is how
 *   "we run their whole feed" is shown. Instagram embeds a profile as its header and a grid
 *   of recent posts. LinkedIn has no public profile embed, so a LinkedIn profile becomes a
 *   link out rather than a frame.
 *
 * Nothing here trusts the input: an editor can paste anything into a text field, so a URL
 * that does not match returns null and the caller renders nothing rather than framing an
 * arbitrary third-party page.
 */

export type SocialPlatform = "instagram" | "linkedin";

export interface SocialPost {
  platform: SocialPlatform;
  url: string;
}

/** Instagram post, reel or TV. The shortcode is the path segment after p/reel/tv. */
const INSTAGRAM_POST = /^https?:\/\/(?:www\.)?instagram\.com\/(?:[^/]+\/)?(?:p|reel|reels|tv)\/([A-Za-z0-9_-]+)/i;

/** Instagram account: instagram.com/<username>, and nothing that is a reserved path. */
const INSTAGRAM_PROFILE = /^https?:\/\/(?:www\.)?instagram\.com\/([A-Za-z0-9._]{1,30})\/?(?:[?#].*)?$/i;
const INSTAGRAM_RESERVED = new Set(["p", "reel", "reels", "tv", "explore", "stories", "accounts", "direct", "about"]);

/**
 * LinkedIn. A shared post URL carries the activity id in its slug, as
 * `...-activity-7123456789-abcd`, and the embed endpoint takes that as a URN. The
 * `/embed/feed/update/urn:li:...` form is also accepted, for a URL copied straight out of
 * LinkedIn's own "Embed this post" dialog.
 */
const LINKEDIN_ACTIVITY = /activity-(\d{6,})/i;
const LINKEDIN_URN = /urn:li:(activity|share|ugcPost):(\d{6,})/i;
const LINKEDIN_PROFILE = /^https?:\/\/(?:[a-z]{2,3}\.)?linkedin\.com\/(?:company|in|showcase|school)\/([^/?#]+)/i;

/** The Instagram username in a profile URL, or null. */
export const instagramHandle = (url: string): string | null => {
  const handle = url.match(INSTAGRAM_PROFILE)?.[1];
  return handle && !INSTAGRAM_RESERVED.has(handle.toLowerCase()) ? handle : null;
};

/** Whether a link is a single post or a whole account. Null when it is neither. */
export const socialKind = (post: SocialPost): "post" | "profile" | null => {
  if (post.platform === "instagram") {
    if (INSTAGRAM_POST.test(post.url)) return "post";
    return instagramHandle(post.url) ? "profile" : null;
  }
  if (LINKEDIN_URN.test(post.url) || LINKEDIN_ACTIVITY.test(post.url)) return "post";
  return LINKEDIN_PROFILE.test(post.url) ? "profile" : null;
};

/**
 * The URL to put in an iframe, or null when the link cannot be framed.
 *
 * Instagram is asked for its captioned variant so a post embed carries the words as well as
 * the picture. LinkedIn's endpoint has no such option, and no profile embed at all.
 */
export const embedUrl = (post: SocialPost): string | null => {
  if (post.platform === "instagram") {
    const code = post.url.match(INSTAGRAM_POST)?.[1];
    if (code) return `https://www.instagram.com/p/${code}/embed/captioned/`;
    const handle = instagramHandle(post.url);
    return handle ? `https://www.instagram.com/${handle}/embed/` : null;
  }

  const urn = post.url.match(LINKEDIN_URN);
  if (urn) return `https://www.linkedin.com/embed/feed/update/urn:li:${urn[1]}:${urn[2]}`;

  const activity = post.url.match(LINKEDIN_ACTIVITY)?.[1];
  return activity ? `https://www.linkedin.com/embed/feed/update/urn:li:activity:${activity}` : null;
};

/** Drops anything this file does not recognise, so nothing renders an empty box. */
export const usable = (posts: SocialPost[]): SocialPost[] => posts.filter((post) => socialKind(post) !== null);
